"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { WordChips } from "@/components/tools/vocabulary/word-parts";
import { WordListStatus } from "@/components/tools/word-games/word-list-status";
import { useLexicon } from "@/components/tools/word-knowledge/use-lexicon";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type Band,
  type Profile,
  profileText,
} from "@/lib/tool-kit/word-knowledge/vocabulary-profile";

/** English strings. A translated content file overrides these through `ui`. */
export const VOCABULARY_PROFILER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste an essay, an abstract or any English text…",
  privacy:
    "Runs entirely in your browser — your text is never uploaded. The word lists download once per visit.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "Sleep deprivation among adolescents is widely linked to poorer attention, but the mechanism remains contested. We argue that chronotype, the natural preference for morning or evening activity, moderates this relationship. Using a longitudinal design, we followed 240 students over two terms and recorded their sleep with actigraphy. Evening types showed greater variability in school-night sleep and lower marks, even after controlling for socioeconomic status. These findings complicate simple recommendations about earlier bedtimes.",
  skipNames:
    "Leave out names and acronyms (capitalized mid-sentence words on no list)",
  enterPrompt:
    "Paste or type some English text to see which frequency band each of its words belongs to.",
  loading: "Loading the word lists (once per visit)…",
  loadError:
    "Could not load the word lists. Check your connection and try again.",
  retry: "Try again",
  results: "Vocabulary profile",
  statGeneral: "General English (NGSL)",
  statAcademic: "Academic (NAWL)",
  statOff: "Off-list",
  statWords: "Running words",
  bandNames: {
    first: "NGSL words 1–1,000",
    second: "NGSL words 1,001–2,000",
    third: "NGSL words 2,001–2,809",
    supplementary: "NGSL days, months and numbers",
    academic: "Academic words (NAWL)",
    off: "Off-list words",
  } as Record<Band, string>,
  colBand: "Band",
  colWords: "Running words",
  colCoverage: "Coverage",
  colCumulative: "Cumulative",
  colDistinct: "Different words",
  tableCaption:
    "Share of the running words that fall in each band, most frequent band first",
  namesLeftOut:
    "one:{count} name or acronym was left out of the count.|other:{count} names or acronyms were left out of the count.",
  coverageNote:
    "Coverage is the share of running words, repeats included, found in each band. “Different words” counts list entries (a word with its inflections is one), or distinct words for the off-list band.",
  offHeading: "Off-list words",
  offIntro:
    "Words on neither list, most frequent first. They are the ones a reader working from the lists alone would meet as unknown: technical terms, rare words, informal forms, misspellings.",
  noOff: "Every counted word is on one of the two lists.",
  moreOff: "and {count} more (copy the list to see them all)",
  copyOff: "Copy off-list words",
  copied: "Copied!",
  academicHeading: "Academic words used",
  noAcademic: "No academic-list words were found.",
  times: "{word} ×{count}",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with academic and off-list words highlighted",
  legend: "Academic words are shaded violet and off-list words amber.",
  source:
    "Word lists: the New General Service List 1.2 and the New Academic Word List 1.2 by Browne, Culligan & Phillips, licensed CC BY-SA 4.0 and adapted here (details below). Bands within the NGSL are cut at frequency ranks 1,000 and 2,000.",
} as const;

type Strings = typeof VOCABULARY_PROFILER_STRINGS;

const MAX_TEXT = 100_000;
const SHOWN_OFF = 80;
const SHOWN_ACADEMIC = 60;
const TONES = { academic: "violet", off: "amber" } as const;
const BAR: Record<Band, string> = {
  first: "bg-emerald-600",
  second: "bg-emerald-500",
  third: "bg-emerald-400",
  supplementary: "bg-sky-400",
  academic: "bg-violet-500",
  off: "bg-amber-500",
};

function percent(fmt: ReturnType<typeof useToolNumber>, value: number): string {
  return fmt(value, { style: "percent", maximumFractionDigits: 1 });
}

function Bar({ profile, strings }: { profile: Profile; strings: Strings }) {
  return (
    <div
      aria-hidden="true"
      className="flex h-4 w-full overflow-hidden rounded-full border border-border"
    >
      {profile.rows
        .filter((row) => row.coverage > 0)
        .map((row) => (
          <div
            className={BAR[row.band]}
            key={row.band}
            style={{ width: `${row.coverage * 100}%` }}
            title={strings.bandNames[row.band]}
          />
        ))}
    </div>
  );
}

function WordLists({
  profile,
  strings,
}: {
  profile: Profile;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const counts = new Map(
    [...profile.offList, ...profile.academic].map((entry) => [
      entry.word,
      entry.count,
    ])
  );
  const label = (word: string) => {
    const count = counts.get(word) ?? 1;
    return count > 1 ? t(strings.times, { word, count: fmt(count) }) : word;
  };
  const off = profile.offList.slice(0, SHOWN_OFF).map((entry) => entry.word);
  const academic = profile.academic
    .slice(0, SHOWN_ACADEMIC)
    .map((entry) => entry.word);
  return (
    <>
      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h3 className={WIDGET_H3}>{strings.offHeading}</h3>
          <CopyButton
            copiedLabel={strings.copied}
            disabled={profile.offList.length === 0}
            label={strings.copyOff}
            small
            text={profile.offList.map((entry) => entry.word).join("\n")}
          />
        </div>
        <p className={HINT}>{strings.offIntro}</p>
        {off.length === 0 ? (
          <Notice>{strings.noOff}</Notice>
        ) : (
          <WordChips format={label} words={off} />
        )}
        {profile.offList.length > SHOWN_OFF ? (
          <p className={HINT}>
            {t(strings.moreOff, {
              count: fmt(profile.offList.length - SHOWN_OFF),
            })}
          </p>
        ) : null}
      </section>
      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.academicHeading}</h3>
        {academic.length === 0 ? (
          <Notice>{strings.noAcademic}</Notice>
        ) : (
          <WordChips format={label} words={academic} />
        )}
      </section>
    </>
  );
}

function Report({
  profile,
  text,
  strings,
}: {
  profile: Profile;
  text: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const row = (band: Band) => profile.rows.find((entry) => entry.band === band);
  const general = profile.rows
    .filter((entry) => !(entry.band === "academic" || entry.band === "off"))
    .reduce((sum, entry) => sum + entry.coverage, 0);
  const highlights: TextMatch[] = profile.matches.map((match) => ({
    start: match.start,
    end: match.end,
    category: match.band,
  }));
  return (
    <section className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <div aria-live="polite" className="space-y-4">
        <StatGrid>
          <StatCard
            emphasis
            label={strings.statGeneral}
            value={percent(fmt, general)}
          />
          <StatCard
            label={strings.statAcademic}
            value={percent(fmt, row("academic")?.coverage ?? 0)}
          />
          <StatCard
            label={strings.statOff}
            value={percent(fmt, row("off")?.coverage ?? 0)}
          />
          <StatCard label={strings.statWords} value={fmt(profile.tokens)} />
        </StatGrid>
        <Bar profile={profile} strings={strings} />
      </div>
      <DataTable
        caption={strings.tableCaption}
        head={[
          strings.colBand,
          strings.colWords,
          strings.colCoverage,
          strings.colCumulative,
          strings.colDistinct,
        ]}
        rows={profile.rows.map((entry) => [
          strings.bandNames[entry.band],
          fmt(entry.tokens),
          percent(fmt, entry.coverage),
          percent(fmt, entry.cumulative),
          fmt(entry.distinct),
        ])}
      />
      <p className={HINT}>{strings.coverageNote}</p>
      {profile.names > 0 ? (
        <p className={HINT}>
          {tPlural(strings.namesLeftOut, locale, profile.names, {
            count: fmt(profile.names),
          })}
        </p>
      ) : null}
      <WordLists profile={profile} strings={strings} />
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
        <HighlightedText
          label={strings.highlightLabel}
          matches={highlights}
          text={text}
          tones={TONES}
        />
        <p className={HINT}>{strings.legend}</p>
      </div>
    </section>
  );
}

export default function VocabularyProfilerWidget() {
  const strings = useToolUi(VOCABULARY_PROFILER_STRINGS);
  const [text, setText] = useState("");
  const [skipNames, setSkipNames] = useState(true);
  const deferred = useDeferredValue(text);
  const hasText = deferred.trim().length > 0;
  const { lexicon, status, retry } = useLexicon(hasText);
  const profile = useMemo(
    () =>
      lexicon && hasText ? profileText(deferred, lexicon, { skipNames }) : null,
    [lexicon, deferred, hasText, skipNames]
  );

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="vp-text"
        label={strings.textLabel}
        onChange={(value) => setText(value.slice(0, MAX_TEXT))}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <Checkbox
        checked={skipNames}
        label={strings.skipNames}
        onChange={setSkipNames}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {hasText ? null : <Notice>{strings.enterPrompt}</Notice>}
      <WordListStatus
        error={strings.loadError}
        loading={strings.loading}
        onRetry={retry}
        retry={strings.retry}
        status={status}
      />
      {profile ? (
        <Report profile={profile} strings={strings} text={deferred} />
      ) : null}
      <p className={HINT}>{strings.source}</p>
    </div>
  );
}
